import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { MessageService } from './message.service';
import { Observable, Subscription, throwError } from 'rxjs';
import { ConfigLoaderService } from '../config/configloader.service';
import { Vote } from '../models/vote';
import { MoodQuestion } from '../models/moodQuestion';
import { catchError, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { SynchronizationService } from './synchronization.service';
import { Subject } from 'rxjs';
import { DatePipe } from '@angular/common';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  })
};

export enum teamMoodsResultType {
  showQuestions = 'showQuestions',
  showVotes = 'showVotes',
}

export enum teamMoodsResultChart {
  line = 'line',
  bubble = 'bubble',
}

@Injectable()
export class RatingService implements OnDestroy {
  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private syncService: SynchronizationService,
    private configService: ConfigLoaderService,
    private translate: TranslateService) {
    this.projectBaseURL = `${configService.settings.pyWallServer.url}projects`;
    this.setDisplayedPerson();
    this.setRTESubscriptions();
  }

  private datePipe = new DatePipe('en-US');
  private event: Subscription[] = [];
  private projectBaseURL: string;
  private newVote = new Subject();
  private voteSettingsChanged = new Subject();

  public newTypeSelection: Subject<string> = new Subject();
  public newChartSelection: Subject<string> = new Subject();
  public newQuestionSelection: Subject<string> = new Subject();

  private typeSelection: teamMoodsResultType = teamMoodsResultType.showVotes;
  private selectedChartType: teamMoodsResultChart = teamMoodsResultChart.line;
  private displayedPerson: string = null;

  private selectedSprintId: string = localStorage.getItem('sprintId');
  private selectedStartDate: Date = null;
  private selectedEndDate: Date = null;
  private selectedPeriodType: string = null;
  private selectedQuestion = 'all';
  private selectedPerson = 'all';
  private selectedValuesChanged = new Subject<boolean>();

  /**
   * components can subscribe to this observable to be notified when settings change and they need to reload data
   * possible events -> new Vote published, Team mood widget settins changed, rating service selected vars changed
   * @returns {Observable<void>}
   */
  newVoteObservable = this.newVote.asObservable();
  voteSettingsChangedObservable = this.voteSettingsChanged.asObservable();
  updateSelectedProps = this.selectedValuesChanged.asObservable();

  public typeSelectionObserver = this.newTypeSelection.asObservable();
  public chartSelectionObserver = this.newChartSelection.asObservable();
  public selectedQuestionObserver = this.newQuestionSelection.asObservable();

  ngOnDestroy(): void {
    for (const ev of this.event) {
      ev.unsubscribe();
    }
  }

  setRTESubscriptions() {
    this.event.push(this.syncService.subscribeEvent('teamMoodSelectionChanged').subscribe(data => {
      this.setTypeSelection(teamMoodsResultType[data.toString()]);
      this.emitSelectionChange();
    }));
    this.event.push(this.syncService.subscribeEvent('teamMoodChartTypeChanged').subscribe(data => {
      this.setSelectedChartType(teamMoodsResultChart[data.toString()]);
      this.emitSelectionChange();
    }));
    this.event.push(this.syncService.subscribeEvent('teamMoodPeriodChanged').subscribe(data => {
      let reload = true;
      if (data.toString() === 'datepicker') { reload = false; }
      this.setSelectedSprintId(data.toString());
      this.emitUpdateSelectedProps(reload);
    }));
    this.event.push(this.syncService.subscribeEvent('teamMoodPersonChanged').subscribe(data => {
      this.setSelectedPerson(data.toString());
      this.emitUpdateSelectedProps(true);
    }));
    this.event.push(this.syncService.subscribeEvent('teamMoodQuestionChanged').subscribe(data => {
      this.setSelectedQuestion(data.toString());
      this.emitUpdateSelectedProps(true);
      this.emitSelectionChange();
    }));
    this.event.push(this.syncService.subscribeEvent('teamMoodDateChanged').subscribe(data => {
      const start: Date = new Date(data['begin']);
      const end: Date = new Date(data['end']);
      this.setSelectedStartDate(start);
      this.setSelectedEndDate(end);
      this.emitUpdateSelectedProps(true);
    }));
  }

  getTypeSelection() {
    return this.typeSelection;
  }

  setTypeSelection(value: any) {
    this.typeSelection = value;
  }

  getSelectedChartType() {
    return this.selectedChartType;
  }

  setSelectedChartType(value: any) {
    this.selectedChartType = value;
  }

  getSelectedPerson(): string {
    return this.selectedPerson;
  }

  setSelectedPerson(value: string) {
    this.selectedPerson = value;
    this.setDisplayedPerson();
  }

  getDisplayedPerson(): string {
    return this.displayedPerson;
  }

  private setDisplayedPerson() {
    if (this.selectedPerson === 'all') {
      this.displayedPerson = this.translate.instant('widgets.teamMood.wholeTeam');
    } else if (this.selectedPerson === 'anonym') {
      this.displayedPerson = this.translate.instant('widgets.teamMood.anonym');
    } else {
      this.displayedPerson = this.selectedPerson;
    }
  }

  getSelectedQuestion(): string {
    return this.selectedQuestion;
  }

  setSelectedQuestion(value: string) {
    this.selectedQuestion = value;
  }

  getSelectedSprintId(): string {
    return this.selectedSprintId;
  }

  getSelectedPeriodType(): string {
    return this.selectedPeriodType;
  }

  setSelectedSprintId(value: string) {
    if (value === 'wholeProject') {
      this.selectedPeriodType = value;
    } else if (value === 'datepicker') {
      this.selectedPeriodType = value;
    } else {
      this.selectedPeriodType = 'sprint';
    }

    if (value === 'wholeProject' || value === 'datepicker') {
      this.selectedSprintId = null;
      this.selectedStartDate = null;
      this.selectedEndDate = null;
    } else {
      this.selectedSprintId = value;
      this.selectedStartDate = null;
      this.selectedEndDate = null;
    }
  }

  getSelectedStartDate(): Date {
    return this.selectedStartDate;
  }

  setSelectedStartDate(startDate: Date) {
    this.selectedStartDate = startDate;
  }

  getSelectedEndDate(): Date {
    return this.selectedEndDate;
  }

  setSelectedEndDate(endDate: Date) {
    this.selectedEndDate = endDate;
  }

  sendNewVote() {
    // Subject.next() now needs 1 Parameter. Please check if the right parameter is given. DZ 13/08/2021
    this.newVote.next(this.newVote);
  }

  sendVoteSettingsChanged() {
    // Subject.next() now needs 1 Parameter. Please check if the right parameter is given. DZ 13/08/2021
    this.voteSettingsChanged.next(this.voteSettingsChanged);
  }

  emitUpdateSelectedProps(reload: boolean) {
    this.selectedValuesChanged.next(reload);
  }

  emitSelectionChange() {
    // Subject.next() now needs 1 Parameter. Please check if the right parameter is given. DZ 13/08/2021
    this.newChartSelection.next(this.selectedChartType);
    this.newTypeSelection.next(this.typeSelection);
    this.newQuestionSelection.next(this.selectedQuestion);
  }

  private handleError(error: HttpErrorResponse) {
    console.error(`Backend returned code ${error.status}, body was: ${error.error}`);
    return throwError('Contact system admin!');
  }

  getAllQuestions(projectKey: string): Observable<MoodQuestion[]> {
    this.messageService.sendDebug('RatingService getAllQuestions called');
    const targetURL = this.projectBaseURL + `/${projectKey}/widgets/team/mood/questions/`;
    const apiResult: Observable<MoodQuestion[]> = this.http.get<MoodQuestion[]>(targetURL, httpOptions);
    const mappedResult = apiResult.pipe(
      catchError(this.handleError),
      map((questions: MoodQuestion[]) => questions.map(question => this.mapJSONToQuestion(question)))
    );
    return mappedResult;
  }

  saveQuestion(projectKey: string, question: MoodQuestion[]): Observable<MoodQuestion> {
    this.messageService.sendDebug(`RatingService saveQuestion called on project ${projectKey}`);
    const targetURL = this.projectBaseURL + `/${projectKey}/widgets/team/mood/questions/`;
    return this.http.post<MoodQuestion>(targetURL, question).pipe(map(res => this.mapJSONToQuestion(res)));
  }

  updateQuestion(projectKey: string, question: MoodQuestion[]): Observable<MoodQuestion> {
    this.messageService.sendDebug(`RatingService updateQuestion called on project ${projectKey}`);
    const targetURL = this.projectBaseURL + `/${projectKey}/widgets/team/mood/questions/`;
    return this.http.put<MoodQuestion>(targetURL, question).pipe(map(res => this.mapJSONToQuestion(res)));
  }

  getInitStandardQuestions(projectKey: string): Observable<MoodQuestion[]> {
    this.messageService.sendDebug(`RatingService initStandardQuestions called ${projectKey}`);
    const targetURL = this.projectBaseURL + `/${projectKey}/widgets/team/mood/questions/init/`;
    const apiResult: Observable<MoodQuestion[]> = this.http.get<MoodQuestion[]>(targetURL);
    const mappedResult = apiResult.pipe(
      catchError(this.handleError),
      map((questions: MoodQuestion[]) => questions.map(question => this.mapJSONToQuestion(question)))
    );
    return mappedResult;
  }

  saveVote(projectkey: string, vote: Vote): Observable<Vote> {
    this.messageService.sendDebug('RatingService saveRating called on rating');
    const targetURL = this.projectBaseURL + `/${projectkey}/widgets/team/mood/vote/`;
    return this.http.post<Object>(targetURL, vote, httpOptions)
      .pipe(map(res => this.mapJSONToVote(res)));
  }

  /**
   * Returns the avg per question for the whole sprint
   * @param projectIdentifier
   * @param sprintId
   * @param person specify for which person. 'all' for any person, includes anonym. 'anonym' for ratings with no person
   * @param startDate is needed when the time period is not equals a sprint
   * @param endDate is needed when the time period is not equals a sprint
   */
  getAvgSprint(projectIdentifier: string, sprintID?: string, person?: string, startDate?: Date, endDate?: Date): Observable<Object> {
    this.messageService.sendDebug('RatingService getAvgSprint called on rating');
    const params: HttpParams = this.setRequestParams(sprintID, person, startDate, endDate);
    const targetURL = this.projectBaseURL + `/${projectIdentifier}/widgets/team/mood/avgSprint/`;
    return this.http.get<Object>(targetURL, { params: params });
  }

  /**
   * Returns the avg per day from all votes
   * @param projectKey
   * @param sprintId
   */
  getAvgPerDayForSprint(projectKey: string, sprintId: string): Observable<Object> {
    this.messageService.sendDebug('RatingService getAvgSprint called on rating');
    const targetURL = this.projectBaseURL + `/${projectKey}/widgets/team/mood/charts/line/dailyAvg/${sprintId}/`;
    return this.http.get<Object>(targetURL);
  }

  /**
   * Returns the avg per sprint from all votes
   * @param projectId
   */
  getAvgPerSprintForProject(projectKey: string): Observable<Object> {
    this.messageService.sendDebug('RatingService getAvgPerSprintForProject called on rating');
    const targetURL = this.projectBaseURL + `/${projectKey}/widgets/team/mood/charts/line/avgPerSprint/`;
    return this.http.get<Object>(targetURL);
  }

  /**
   * Returns all ratings as bubble chart, grouped per day. Ratings with the same value increase the bubble size
   * @param projectId
   * @param sprintId
   * @param person specify for which person. 'all' for any person, includes anonym. 'anonym' for ratings with no person
   * @param startDate is needed when the time period is not equals a sprint
   * @param endDate is needed when the time period is not equals a sprint
   */
  getRatingsForBubble(projectId: number, sprintID?: string, person?: string, startDate?: Date, endDate?: Date): Observable<Object> {
    this.messageService.sendDebug('RatingService getAvgSprint called on rating');
    const params: HttpParams = this.setRequestParams(sprintID, person, startDate, endDate);
    const targetURL = this.projectBaseURL + `/${projectId}/widgets/team/mood/charts/bubble/ratings/`;
    return this.http.get<Object>(targetURL, { params: params });
  }

  /**
   * Reatuns for each questions the daily average
   * @param projectKey
   * @param sprintId
   * @param person specify for which person. 'all' for any person, includes anonym. 'anonym' for ratings with no person
   * @param startDate is needed when the time period is not equals a sprint
   * @param endDate is needed when the time period is not equals a sprint
   */
  getRatingsForLine(projectKey: string, startDate: Date, endDate: Date, person?: string): Observable<Object> {
    this.messageService.sendDebug('RatingService getAvgSprint called on rating');
    const params: HttpParams = this.setRequestParams(null, person, startDate, endDate);
    const targetURL = this.projectBaseURL + `/${projectKey}/widgets/team/mood/question/charts/line/ratings/`;
    return this.http.get<Object>(targetURL, { params: params });
  }

  /**
   * Reatuns for each day the vote count
   * @param projectKey
   * @param sprintId
   * @param person specify for which person. 'all' for any person, includes anonym. 'anonym' for ratings with no person
   * @param startDate is needed when the time period is not equals a sprint
   * @param endDate is needed when the time period is not equals a sprint
   */
  getVoteCountByDay(projectKey: string, startDate: Date, endDate: Date, person?: string): Observable<Object> {
    this.messageService.sendDebug('RatingService getVoteCountByDay called on rating');
    const params: HttpParams = this.setRequestParams(null, person, startDate, endDate);
    const targetURL = this.projectBaseURL + `/${projectKey}/widgets/team/mood/vote/countByDay/`;
    return this.http.get<Object>(targetURL, { params: params });
  }

  /**
   * Returns all votes as dataset for bubble chart, grouped per day. Ratings with the same value increase the bubble size
   * @param projectId
   * @param sprintId
   * @param person specify for which person. 'anonym' for ratings with no person. 'all' for any person, includes anonym.
   * @param startDate is needed when the time period is not equals a sprint
   * @param endDate is needed when the time period is not equals a sprint
   */
  getVotesForBubble(projectId: number, sprintID?: string, person?: string, startDate?: Date, endDate?: Date): Observable<Object> {
    this.messageService.sendDebug('RatingService getAvgSprint called on rating');
    const params: HttpParams = this.setRequestParams(sprintID, person, startDate, endDate);
    const targetURL = this.projectBaseURL + `/${projectId}/widgets/team/mood/charts/bubble/votes/`;
    return this.http.get<Object>(targetURL, { params: params });
  }

  /**
   * Returns all questions as bubble chart, grouped per questions. Ratings with the same value increase the bubble size
   * @param projectId
   * @param sprintId
   * @param person specify for which person. 'anonym' for ratings with no person. 'all' for any person, includes anonym.
   * @param startDate is needed when the time period is not equals a sprint
   * @param endDate is needed when the time period is not equals a sprint
   */
  getQuestionsForBubble(projectId: number, sprintID?: string, person?: string, startDate?: Date, endDate?: Date): Observable<Object> {
    this.messageService.sendDebug('RatingService getAvgSprint called on rating');
    const params: HttpParams = this.setRequestParams(sprintID, person, startDate, endDate);
    const targetURL = this.projectBaseURL + `/${projectId}/widgets/team/mood/charts/bubble/questions/`;
    return this.http.get<Object>(targetURL, { params: params });
  }

  /**
   * Returns feedback
   * @param project_key
   * @param sprintId
   * @param person specify for which person. 'all' for any person, includes anonym. 'anonym' for ratings with no person
   * @param startDate is needed when the time period is not equals a sprint
   * @param endDate is needed when the time period is not equals a sprint
   */
  getFeedbackSprint(project_key: string, sprintID?: string, person?: string, startDate?: Date, endDate?: Date): Observable<any> {
    this.messageService.sendDebug('RatingService getAvgSprint called on rating');
    const params: HttpParams = this.setRequestParams(sprintID, person, startDate, endDate);
    const targetURL = this.projectBaseURL + `/${project_key}/widgets/team/mood/feedback/`;
    return this.http.get<Object>(targetURL, { params: params });
  }

  getLastVoteForSprint(projectKey: string, sprintId: string): Observable<Vote> {
    this.messageService.sendDebug('RatingService getLastVoteForSprint called on rating');
    let params = new HttpParams();
    params = params.append('sprintID', sprintId);
    const targetURL = this.projectBaseURL + `/${projectKey}/widgets/team/mood/lastVote/`;
    return this.http.get<Vote>(targetURL, { params: params }).pipe(map(res => this.mapJSONToVote(res)));
  }

  getVotesSprint(projectkey: string, sprintId: string): Observable<Vote[]> {
    this.messageService.sendDebug('RatingService getAvgSprint called on rating');
    const targetURL = this.projectBaseURL + `/${projectkey}/widgets/team/mood/vote/`;
    const params = (new HttpParams()).append('sprintID', sprintId);
    const apiResult: Observable<Vote[]> = this.http.get<Vote[]>(targetURL, { params: params });
    const mappedResult = apiResult.pipe(
      catchError(this.handleError),
      map((votes: Vote[]) => votes.map(vote => this.mapJSONToVote(vote)))
    );
    return mappedResult;
  }

  setRequestParams(sprintID, person, startDate: Date, endDate: Date): HttpParams {
    let params = new HttpParams();
    if (sprintID) {
      params = params.append('sprintID', sprintID);
    }
    if (person) {
      params = params.append('person', person);
    }
    if (startDate) {
      params = params.append('startDate', this.convertDateToString(startDate));
    }
    if (endDate) {
      params = params.append('endDate', this.convertDateToString(endDate));
    }
    return params;
  }

  convertDateToString(date: Date): string {
    return this.datePipe.transform(date, 'dd/MM/yyyy');
  }

  mapJSONToVote(data: any): Vote {
    if (data != null) {
      return new Vote(data['id'], data['sprintId'], data['ratings'], data['comment'], data['person'], data['date']);
    }
    else { return null; }
  }

  mapJSONToQuestion(data: any): MoodQuestion {
    return new MoodQuestion(data['id'], data['name'], data['color'], data['projectId'], data['active'], data['deleted']);
  }

  /**
   * Sorts foreach object in the array form {r, x, y} to {x, y, r}
   * @param res Array of objects to be sorted
   * @param rMultiplier multiplies the r value with it
   */
  sortJsonArrayForBubbleChartVotes(res: any[], rMultiplier = 1) {
    const dataSet = [];
    for (const inner of res) {
      if (inner.length > 0) {
        for (let i = 0; i < inner.length; i++) {
          const d = inner[i];
          const obj = { x: d.x, y: d.y, r: (d.r * rMultiplier) };
          dataSet.push(obj);
        }
      }
    }
    return dataSet;
  }

  /**
   * Sorts foreach object in the array form {r, x, y} to {x, y, r}
   * @param res Array of objects to be sorted
   * @param rMultiplier multiplies the r value with it
   */
  sortJsonArrayForBubbleChartQuestions(res: any[], rMultiplier = 1) {
    const dataSet = [];
    for (let i = 0; i < res.length; i++) {
      const d = res[i];
      const obj = { x: d.x, y: d.y, r: (d.r * rMultiplier) };
      dataSet.push(obj);
    }
    return dataSet;
  }

  /**
   * Formate the date to a shorter version DD-MM-YY for charts
   * @param res Array of objects to be sorted
   * @param translate translateService which should be used
   */
  shortDaysFormat(dates: any[], translate: TranslateService) {
    const shortDays = [];
    for (const day of dates) {
      const longDay = new Date(day);
      // TODO options properties-types seem to be broken after update of angular (incl. typescript)
      const options = { weekday: 'short', year: '2-digit', month: '2-digit', day: '2-digit' };
      shortDays.push(longDay.toLocaleDateString(translate.currentLang /* , options */));
    }
    return shortDays;
  }
}
